import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// What each unit costs, from the recommended steps. All estimates.
const UNITS = [
  { key: "courts", label: "Stackable 5-a-side / futsal courts", cost: 23750, note: "$950k for the first 40-court tranche" },
  { key: "indoor", label: "Indoor football arenas", cost: 1200000, note: "Community-scale indoor arena, estimate" },
  { key: "stadiums", label: "Modular professional stadiums", cost: 5500000, note: "Midpoint of the $3–8m build estimate" },
  { key: "badges", label: "Coach badges with mentoring", cost: 2200, note: "Per coach, including mentoring" },
  { key: "academies", label: "County academy programmes (one year)", cost: 135000, note: "Coaching, kit, transport, welfare" },
];

const PLAN_TOTAL = 3_682_000; // indicative total across the recommended steps

const money = (n) =>
  n >= 1_000_000_000
    ? `$${(n / 1_000_000_000).toFixed(2)}bn`
    : n >= 1_000_000
    ? `$${(n / 1_000_000).toFixed(1)}m`
    : `$${Math.round(n).toLocaleString()}`;

export const DiasporaImpact = ({ series = [] }) => {
  const years = useMemo(
    () => series.filter((r) => r.remittances_usd_bn && r.year <= 2026).map((r) => r.year),
    [series]
  );
  const [year, setYear] = useState(null);
  const [share, setShare] = useState(1);

  const activeYear = year || years[years.length - 1];
  const row = series.find((r) => r.year === activeYear) || {};
  const pot = (row.remittances_usd_bn || 0) * 1e9 * (share / 100);

  if (!years.length) return null;

  return (
    <div className="border border-maasai/50 bg-[#0d0304] p-7 md:p-11" data-testid="diaspora-impact">
      <p className="overline text-maasai">The diaspora comparison</p>
      <h2 className="display-2 mt-4 max-w-3xl">What one percent would build</h2>
      <p className="body-copy mt-5 max-w-3xl">
        Kenyans abroad send home more every year than any investor, donor or sponsor puts into the country. This is not
        an appeal — it is a comparison. Move the slider and see what a slice of a single year&rsquo;s remittances would
        buy against the estimates in the recommended steps.
      </p>

      <div className="mt-9 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">Remittance year</p>
              <div className="mt-3 flex flex-wrap gap-1" data-testid="diaspora-years">
                {years.map((y) => (
                  <button
                    key={y}
                    onClick={() => setYear(y)}
                    data-testid={`diaspora-year-${y}`}
                    className={`border px-3 py-2 text-[0.6rem] font-bold uppercase tracking-[0.14em] transition-colors ${
                      activeYear === y ? "border-ochre text-ochre" : "border-white/10 text-zinc-500 hover:text-white"
                    }`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">
                Sent home in {activeYear}
              </p>
              <p className="mono mt-3 text-2xl text-white">
                ${row.remittances_usd_bn}bn
                <span className="ml-2 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400">{row.status}</span>
              </p>
            </div>
          </div>

          <div className="mt-9">
            <div className="flex items-baseline justify-between">
              <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">
                Share redirected to football infrastructure
              </p>
              <p className="mono text-lg text-ochre" data-testid="diaspora-share">
                {share}%
              </p>
            </div>
            <input
              type="range"
              min="0.1"
              max="5"
              step="0.1"
              value={share}
              onChange={(e) => setShare(Number(e.target.value))}
              data-testid="diaspora-slider"
              aria-label="Share of remittances redirected to football infrastructure"
              className="mt-4 w-full accent-[#E32118]"
            />
            <p className="mt-5 font-display text-4xl font-bold leading-none text-white md:text-5xl" data-testid="diaspora-pot">
              {money(pot)}
            </p>
            <p className="mt-3 text-[0.6rem] uppercase tracking-[0.16em] text-ochre">
              {(pot / PLAN_TOTAL).toFixed(1)}× the indicative cost of all the recommended steps
            </p>

            <div className="mt-8 space-y-4" data-testid="diaspora-comparisons">
              {[
                ["All recommended steps", PLAN_TOTAL],
                ["One modular professional stadium (midpoint estimate)", 5500000],
                ["Professional stadiums in all 18 proposed counties", 5500000 * 18],
              ].map(([label, cost]) => {
                const pct = Math.min(100, (pot / cost) * 100);
                return (
                  <div key={label}>
                    <div className="flex items-baseline justify-between gap-4">
                      <span className="text-[0.62rem] uppercase tracking-[0.14em] text-zinc-500">{label}</span>
                      <span className="mono text-xs text-white">
                        {pot >= cost ? `${(pot / cost).toFixed(1)}× covered` : `${pct.toFixed(0)}% covered`}
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 w-full bg-white/10">
                      <div
                        className="h-full bg-maasai transition-[width] duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border border-white/10 bg-black p-6" data-testid="diaspora-buys">
          <p className="overline">That would build</p>
          <ul className="mt-5 space-y-4">
            {UNITS.map((u) => (
              <li key={u.key} data-testid={`diaspora-unit-${u.key}`}>
                <p className="mono text-xl text-ochre">{Math.floor(pot / u.cost).toLocaleString()}</p>
                <p className="mt-1 text-sm text-white">{u.label}</p>
                <p className="text-[0.56rem] uppercase tracking-[0.14em] text-zinc-400">{u.note}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="mt-8 max-w-3xl text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
        Unit costs are the estimates published in the recommended steps. Remittance totals are Central Bank of
        Kenya figures for complete years and estimates thereafter. This is a comparison, not a fundraising target.
      </p>

      <div className="mt-8 flex flex-wrap gap-4">
        <Link to="/apply" className="btn-primary" data-testid="diaspora-proposal-cta">
          Propose how to spend it <ArrowRight size={14} />
        </Link>
        <Link to="/plans" className="btn-ghost">
          The steps
        </Link>
      </div>
    </div>
  );
};
